import React from 'react'
import { AdaptiveCarousel, AdaptiveCarouselSlide, Column, H2, Row, Typography } from '@vp/swan'
import { useStyles } from '@vp/ubik-context'
import { ProductTile, type ExtendedMtsLocalizationResponse } from '@vp/ep-tiles'
import { stylesheet as productTileStylesheet } from '@vp/ep-tiles/product-tile.css'
import { ProductRecommendationItem } from '../../types/Recommendations'
import { getDesktopTilesPerRow } from '../../utils/recommendations/productRecommendationsBuilder'

export interface LaneProps {
  title?: string | undefined
  subtitle?: string | undefined
  products: ProductRecommendationItem[]
  sidebarEnabled: boolean
  pageSection: string
  calloutsLocalization: ExtendedMtsLocalizationResponse
  sectionId?: string | undefined
  onProductTileClick?: (product: ProductRecommendationItem, index: number) => void
  onSlideChange?: (firstSlide: number, lastSlide: number) => void
}

export const Lane = ({
  title,
  subtitle,
  products,
  sidebarEnabled,
  pageSection,
  calloutsLocalization,
  sectionId,
  onProductTileClick,
  onSlideChange
}: LaneProps) => {
  useStyles(productTileStylesheet)

  if (products.length === 0) return null

  const desktopTilesPerRow = getDesktopTilesPerRow(products.length, sidebarEnabled)
  const slidesToShow = {
    xs: 2,
    sm: desktopTilesPerRow,
    md: desktopTilesPerRow,
    lg: desktopTilesPerRow,
    xl: desktopTilesPerRow
  }

  const carouselId = sectionId ?? `recommendations-lane-${pageSection}`
  const carouselTitle = title ?? pageSection
  const headerColSpan: 5 | 7 = sidebarEnabled ? 7 : 5

  return (
    <>
      {(title || subtitle) && (
        <Row mb={4}>
          <Column span={headerColSpan}>
            {title && <H2 fontSize="x2large">{title}</H2>}
            {subtitle && <Typography fontSize="standard">{subtitle}</Typography>}
          </Column>
        </Row>
      )}
      <AdaptiveCarousel
        id={carouselId}
        slidesToShow={slidesToShow}
        initialSlide={1}
        aria-labelledby={carouselId}
        aria-label={carouselTitle}
        onSlideChange={onSlideChange}
      >
        {products.map((product, index) => (
          <AdaptiveCarouselSlide key={product.mpvId}>
            <ProductTile
              data-testid="recommendations-lane-tile"
              data-mpv-id={product.mpvId}
              product={{
                productKey: product.productKey,
                mpvId: product.mpvId,
                productVersion: product.productVersion,
                name: product.name,
                description: product.description,
                productUrl: product.productUrl,
                image: {
                  src: product.image?.src ?? '',
                  alt: product.image?.alt ?? product.name
                },
                inStock: !product.isOutOfStock,
                selectedOptions: product.selectedOptions
              }}
              {...(product.callout !== undefined && { callout: product.callout })}
              ratingSummary={product.ratingSummary}
              calloutsLocalization={calloutsLocalization}
              favourite={{ pageZone: pageSection, productPosition: index }}
              callbacks={{ onTileClick: () => onProductTileClick?.(product, index) }}
            />
          </AdaptiveCarouselSlide>
        ))}
      </AdaptiveCarousel>
    </>
  )
}

export default Lane
